import { useState, useEffect, useCallback } from "react";
import {
  createModalCloseHandler,
  restorePointerEvents,
} from "@/lib/modal-utils";
import {
  Plus,
  Building2,
  Users,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  MoreHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Enhanced Components
import ViewToggle from "./components/ViewToggle";
import SearchBar from "./components/SearchBar";
import FilterSidebar from "./components/FilterSidebar";
import DepartmentCard from "./components/DepartmentCard";
import DepartmentTable from "./components/DepartmentTable";
import DepartmentList from "./components/DepartmentList";
import DepartmentAnalytics from "./components/DepartmentAnalytics";
import EmployeesModal from "./components/EmployeesModal";

// Existing Components
import DepartmentForm from "./DepartmentForm";
import DepartmentDetails from "./DepartmentDetails";
import EmployeeProfile from "../../employees/EmployeeProfile";
import EmployeeForm from "../../employees/EmployeeForm";

// API
import { departmentsAPI, departmentAPI } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

export default function DepartmentsPage() {
  const { toast } = useToast();

  // State Management
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [managers, setManagers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // View & UI State
  const [viewMode, setViewMode] = useState("cards");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Modal State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEmployeesModalOpen, setIsEmployeesModalOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    department: null,
    isHardDelete: false,
  });

  // Employee specific states (for viewing/editing from department modals)
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isEmployeeProfileOpen, setIsEmployeeProfileOpen] = useState(false);
  const [isEmployeeFormOpen, setIsEmployeeFormOpen] = useState(false);

  // Helper Functions
  const handleOpenCreateForm = () => {
    setSelectedDepartment(null);
    setIsFormOpen(true);
    setIsEditing(false);
  };

  const formatCurrency = (amount) => {
    if (!amount) return "Non défini";
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount)) return "Non défini";
    return new Intl.NumberFormat("fr-MA", {
      style: "currency",
      currency: "MAD",
    }).format(numericAmount);
  };

  // Load data on mount
  useEffect(() => {
    loadManagers();
    loadDepartments();
  }, []);

  // Handle page changes
  useEffect(() => {
    loadDepartments();
  }, [currentPage]);

  // Debounced search with useCallback for better performance
  const debouncedSearch = useCallback(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1); // This will trigger loadDepartments via the currentPage useEffect
      } else {
        loadDepartments(); // If already on page 1, load directly
      }
    }, 300); // Reduced debounce time for better responsiveness

    return () => clearTimeout(timer);
  }, [searchTerm, currentPage]);

  useEffect(() => {
    const cleanup = debouncedSearch();
    return cleanup;
  }, [debouncedSearch]);

  // Generate search suggestions
  useEffect(() => {
    if (departments.length > 0) {
      const suggestions = [
        ...new Set([
          ...departments.map((d) => d.nom),
          ...departments.map((d) => d.description).filter(Boolean),
          ...departments
            .filter((d) => d.manager)
            .map((d) => `${d.manager.prenom} ${d.manager.nom}`),
        ]),
      ].slice(0, 10);
      setSearchSuggestions(suggestions);
    }
  }, [departments]);

  // Initialize pointer events monitoring to fix modal focus issues
  useEffect(() => {
    // Force restore pointer events on mount
    restorePointerEvents();

    // Set up monitoring for pointer events issues
    const intervalId = setInterval(() => {
      // Only restore if no modal is currently open
      const openDialogs = document.querySelectorAll(
        '[data-state="open"][role="dialog"]'
      );
      if (openDialogs.length === 0) {
        restorePointerEvents();
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
      };
      const response = await departmentsAPI.getAll(params);
      setDepartments(response.data.data.departments || []);
      setTotalPages(response.data.data.pagination?.pages || 1);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les départements",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadManagers = async () => {
    try {
      const response = await departmentAPI.getAvailableManagers();
      setManagers(response.data.managers || []);
    } catch (error) {
      console.error("Erreur lors du chargement des managers:", error);
    }
  };

  // CRUD Operations
  const handleCreateDepartment = async (data) => {
    try {
      await departmentsAPI.create(data);
      toast({
        title: "Succès",
        description: "Département créé avec succès",
      });
      setIsFormOpen(false);
      loadDepartments();
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error.response?.data?.message || "Erreur lors de la création",
        variant: "destructive",
      });
    }
  };

  const handleUpdateDepartment = async (data) => {
    try {
      await departmentsAPI.update(selectedDepartment.id, data);
      toast({
        title: "Succès",
        description: "Département modifié avec succès",
      });
      setIsFormOpen(false);
      setIsEditing(false);
      setSelectedDepartment(null);
      loadDepartments();
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error.response?.data?.message || "Erreur lors de la modification",
        variant: "destructive",
      });
    }
  };

  const handleDeleteDepartment = (department, hardDelete = false) => {
    setDeleteDialog({
      open: true,
      department,
      isHardDelete: hardDelete,
    });
  };

  const confirmDelete = async () => {
    const { department, isHardDelete } = deleteDialog;

    try {
      if (isHardDelete) {
        await departmentAPI.hardDelete(department.id);
      } else {
        await departmentAPI.delete(department.id);
      }

      toast({
        title: "Succès",
        description: isHardDelete
          ? "Département supprimé définitivement avec succès"
          : department.actif
            ? "Département désactivé avec succès"
            : "Département supprimé avec succès",
        variant: "success",
      });

      loadDepartments();
    } catch (error) {
      console.error("Error deleting department:", error);

      // Provide specific error messages based on the error type
      if (
        error.response?.status === 400 &&
        error.response?.data?.message?.includes("employees")
      ) {
        toast({
          title: "Erreur",
          description: `Impossible de supprimer le département "${department.nom}" car il contient encore des employés. Veuillez d'abord réassigner ou supprimer les employés.`,
          variant: "destructive",
        });
      } else if (
        error.response?.status === 400 &&
        error.response?.data?.message?.includes("actif")
      ) {
        toast({
          title: "Erreur",
          description: `Impossible de supprimer définitivement un département actif. Veuillez d'abord le désactiver.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Erreur",
          description:
            error.response?.data?.message ||
            "Une erreur s'est produite lors de la suppression du département",
          variant: "destructive",
        });
      }
    } finally {
      setDeleteDialog({ open: false, department: null, isHardDelete: false });
    }
  };

  // UI Handlers
  const handleViewDepartment = (department) => {
    setSelectedDepartment(department);
    setIsDetailsOpen(true);
  };

  const handleEditDepartment = (department) => {
    setSelectedDepartment(department);
    setIsEditing(true);
    setIsFormOpen(true);
  };

  const handleViewEmployees = (department) => {
    setSelectedDepartment(department);
    setIsEmployeesModalOpen(true);
  };

  const handleViewEmployeeProfile = (employee) => {
    setSelectedEmployee(employee);
    setIsEmployeeProfileOpen(true);
  };

  const handleEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    setIsEmployeeFormOpen(true);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilterChange = (filters) => {
    setActiveFilters(filters);
  };

  const handleSort = (config) => {
    setSortConfig(config);
  };

  const clearFilters = () => {
    setActiveFilters({});
    setSearchTerm("");
  };

  // Helper function to create filter display objects
  const createFilterDisplayObjects = () => {
    return Object.entries(activeFilters)
      .filter(([key, value]) => value && value !== "all" && value !== "")
      .map(([key, value]) => {
        let label = "";

        switch (key) {
          case "status":
            label = value === "true" ? "Statut: Actif" : "Statut: Inactif";
            break;
          case "employeeCount":
            label = `Employés: ${value}`;
            break;
          case "manager":
            if (value === "has-manager") label = "Avec manager";
            else if (value === "no-manager") label = "Sans manager";
            else label = "Manager spécifique";
            break;
          case "budgetMin":
            label = `Budget min: ${parseInt(value).toLocaleString()}€`;
            break;
          case "budgetMax":
            label = `Budget max: ${parseInt(value).toLocaleString()}€`;
            break;
          case "budgetRange":
            if (value === "0-0") label = "Sans budget";
            else label = `Budget: ${value}`;
            break;
          case "dateRange":
            if (value === "30-days") label = "Nouveaux (30j)";
            else label = `Période: ${value}`;
            break;
          default:
            label = `${key}: ${value}`;
        }

        return {
          key,
          value,
          label,
          onRemove: () => handleRemoveFilter(key),
        };
      });
  };

  const handleRemoveFilter = (filterKey) => {
    const newFilters = { ...activeFilters };
    delete newFilters[filterKey];
    setActiveFilters(newFilters);
  };

  // Filter and sort departments (removed client-side search since we use server-side search)
  const processedDepartments = departments
    .filter((department) => {
      // Only apply non-search filters - search is handled server-side

      // Active filter
      if (activeFilters.status && activeFilters.status !== "all") {
        if (activeFilters.status === "true" && !department.actif) return false;
        if (activeFilters.status === "false" && department.actif) return false;
      }

      // Employee count filter
      if (
        activeFilters.employeeCount &&
        activeFilters.employeeCount !== "all"
      ) {
        const count = department._count?.employes || 0;
        if (activeFilters.employeeCount === "0-10" && (count < 0 || count > 10))
          return false;
        if (
          activeFilters.employeeCount === "11-50" &&
          (count < 11 || count > 50)
        )
          return false;
        if (
          activeFilters.employeeCount === "51-100" &&
          (count < 51 || count > 100)
        )
          return false;
        if (activeFilters.employeeCount === "100+" && count <= 100)
          return false;
      }

      // Budget filter
      if (
        activeFilters.budgetMin ||
        activeFilters.budgetMax ||
        activeFilters.budgetRange
      ) {
        const budget = parseFloat(department.budget) || 0;

        // Handle budget range from quick filters
        if (activeFilters.budgetRange === "0-0" && budget !== 0) return false;

        // Handle min/max budget filters
        if (
          activeFilters.budgetMin &&
          budget < parseFloat(activeFilters.budgetMin)
        )
          return false;
        if (
          activeFilters.budgetMax &&
          budget > parseFloat(activeFilters.budgetMax)
        )
          return false;
      }

      // Manager filter
      if (activeFilters.manager && activeFilters.manager !== "all") {
        if (activeFilters.manager === "has-manager" && !department.manager)
          return false;
        if (activeFilters.manager === "no-manager" && department.manager)
          return false;
        if (
          activeFilters.manager !== "has-manager" &&
          activeFilters.manager !== "no-manager" &&
          department.manager?.id !== activeFilters.manager
        )
          return false;
      }

      return true; // Return true since search filtering is handled server-side
    })
    .sort((a, b) => {
      if (!sortConfig.field) return 0;

      let aValue, bValue;

      switch (sortConfig.field) {
        case "nom":
          aValue = a.nom;
          bValue = b.nom;
          break;
        case "manager":
          aValue = a.manager ? `${a.manager.prenom} ${a.manager.nom}` : "";
          bValue = b.manager ? `${b.manager.prenom} ${b.manager.nom}` : "";
          break;
        case "_count.employes":
          aValue = a._count?.employes || 0;
          bValue = b._count?.employes || 0;
          break;
        case "budget":
          aValue = parseFloat(a.budget) || 0;
          bValue = parseFloat(b.budget) || 0;
          break;
        case "actif":
          aValue = a.actif ? 1 : 0;
          bValue = b.actif ? 1 : 0;
          break;
        case "date_creation":
          aValue = new Date(a.date_creation);
          bValue = new Date(b.date_creation);
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      let result = 0;
      if (aValue < bValue) result = -1;
      if (aValue > bValue) result = 1;

      return sortConfig.direction === "desc" ? -result : result;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Départements</h1>
          <p className="text-gray-600">
            Gérez les départements de votre organisation avec une interface
            moderne et intuitive
          </p>
        </div>
        <Button
          onClick={() => setIsFormOpen(true)}
          className="w-full sm:w-auto"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouveau département
        </Button>
      </div>

      {/* Controls Bar */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Search Bar */}
        <div className="flex-1">
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            suggestions={searchSuggestions}
            onFilterToggle={() => setIsSidebarOpen(!isSidebarOpen)}
            hasActiveFilters={Object.keys(activeFilters).length > 0}
            activeFilters={createFilterDisplayObjects()}
            onClearFilters={clearFilters}
          />
        </div>

        {/* View Toggle */}
        <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
      </div>

      {/* Main Content Area */}
      <div className="flex gap-6">
        {/* Filter Sidebar */}
        <FilterSidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          filters={activeFilters}
          onFiltersChange={handleFilterChange}
          availableManagers={managers}
        />

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Results Summary */}
          <div className="mb-4 flex items-center justify-between text-sm text-gray-600">
            <span>
              {processedDepartments.length} département
              {processedDepartments.length > 1 ? "s" : ""}
              {processedDepartments.length !== departments.length &&
                ` sur ${departments.length}`}
            </span>
            {(searchTerm || Object.keys(activeFilters).length > 0) && (
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Effacer filtres
              </Button>
            )}
          </div>

          {/* Content Views */}
          {processedDepartments.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || Object.keys(activeFilters).length > 0
                  ? "Aucun département trouvé"
                  : "Aucun département"}
              </h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || Object.keys(activeFilters).length > 0
                  ? "Aucun département ne correspond à vos critères de recherche"
                  : "Commencez par créer votre premier département"}
              </p>
              {!(searchTerm || Object.keys(activeFilters).length > 0) && (
                <Button onClick={() => setIsFormOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Créer un département
                </Button>
              )}
            </div>
          ) : (
            <>
              {viewMode === "cards" && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {processedDepartments.map((department) => (
                    <DepartmentCard
                      key={department.id}
                      department={department}
                      onView={handleViewDepartment}
                      onEdit={handleEditDepartment}
                      onDelete={handleDeleteDepartment}
                      onViewEmployees={handleViewEmployees}
                    />
                  ))}
                </div>
              )}

              {viewMode === "table" && (
                <DepartmentTable
                  departments={processedDepartments}
                  onView={handleViewDepartment}
                  onEdit={handleEditDepartment}
                  onDelete={handleDeleteDepartment}
                  onViewEmployees={handleViewEmployees}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              )}

              {viewMode === "list" && (
                <DepartmentList
                  departments={processedDepartments}
                  onView={handleViewDepartment}
                  onEdit={handleEditDepartment}
                  onDelete={handleDeleteDepartment}
                  onViewEmployees={handleViewEmployees}
                />
              )}

              {viewMode === "analytics" && (
                <DepartmentAnalytics departments={processedDepartments} />
              )}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <DepartmentForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setIsEditing(false);
          setSelectedDepartment(null);
        }}
        onSuccess={() => {
          setIsFormOpen(false);
          setIsEditing(false);
          setSelectedDepartment(null);
          loadDepartments();
        }}
        department={isEditing ? selectedDepartment : null}
      />

      <DepartmentDetails
        department={selectedDepartment}
        isOpen={isDetailsOpen}
        onClose={createModalCloseHandler(() => {
          setIsDetailsOpen(false);
          setSelectedDepartment(null);
        })}
        onEdit={handleEditDepartment}
        onDelete={handleDeleteDepartment}
      />

      <EmployeesModal
        department={selectedDepartment}
        isOpen={isEmployeesModalOpen}
        onClose={createModalCloseHandler(() => {
          setIsEmployeesModalOpen(false);
          setSelectedDepartment(null);
        })}
        onViewEmployee={handleViewEmployeeProfile}
        onEditEmployee={handleEditEmployee}
      />

      {/* Employee Profile Modal */}
      {selectedEmployee && (
        <Dialog
          open={isEmployeeProfileOpen}
          onOpenChange={createModalCloseHandler((open) => {
            setIsEmployeeProfileOpen(open);
            if (!open) setSelectedEmployee(null);
          })}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <EmployeeProfile
              employee={selectedEmployee}
              onClose={createModalCloseHandler(() => {
                setIsEmployeeProfileOpen(false);
                setSelectedEmployee(null);
              })}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Employee Edit Modal */}
      {selectedEmployee && (
        <Dialog
          open={isEmployeeFormOpen}
          onOpenChange={createModalCloseHandler((open) => {
            setIsEmployeeFormOpen(open);
            if (!open) setSelectedEmployee(null);
          })}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifier l'employé</DialogTitle>
              <DialogDescription>
                Modifiez les informations de l'employé ci-dessous.
              </DialogDescription>
            </DialogHeader>
            <EmployeeForm
              employee={selectedEmployee}
              onSuccess={createModalCloseHandler(() => {
                setIsEmployeeFormOpen(false);
                setSelectedEmployee(null);
                // Refresh data if needed, but since we're in departments, 
                // we probably don't need to refresh the whole dept list 
                // unless we want to update the manager name display.
                loadDepartments();
              })}
              departments={departments}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) =>
          !open &&
          setDeleteDialog({
            open: false,
            department: null,
            isHardDelete: false,
          })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteDialog.isHardDelete
                ? "Supprimer définitivement"
                : deleteDialog.department?.actif
                  ? "Désactiver le département"
                  : "Supprimer le département"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog.isHardDelete
                ? `Êtes-vous sûr de vouloir supprimer définitivement le département "${deleteDialog.department?.nom}" ? Cette action est irréversible et supprimera toutes les données associées.`
                : deleteDialog.department?.actif
                  ? `Êtes-vous sûr de vouloir désactiver le département "${deleteDialog.department?.nom}" ? Il ne sera plus disponible pour les nouvelles affectations.`
                  : `Êtes-vous sûr de vouloir supprimer le département "${deleteDialog.department?.nom}" ? Cette action peut être annulée plus tard.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className={
                deleteDialog.isHardDelete ? "bg-red-600 hover:bg-red-700" : ""
              }
            >
              {deleteDialog.isHardDelete
                ? "Supprimer définitivement"
                : deleteDialog.department?.actif
                  ? "Désactiver"
                  : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
